/**
 * Вкладка «Программы» каталога программ обучения (component.team-training-programs.programs,
 * centrika-docs!55; training-catalog-v2 — структура каталога заказчицы, ТЗ 26.10.2026, п.4.4.1).
 * Программы — деревом разделов, как у клиента (ответ заказчицы 04.11 к п.4.6: «Каталог сделать
 * древовидным. Соответственно, из карточки убрать Раздел, программа и так должна загружаться сразу
 * в нужный раздел»): раздел раскрывается, у раздела — число программ и «Добавить программу» в
 * него; перенос в другой раздел — действием «Перенести». Поиск (код, наименования) и фильтр по
 * статусу раскрывают разделы с найденным. Колонка «Индекс» — после «Кода» (ответ к п.5.2). Архив
 * вместо удаления (удалить можно только программу, которой нет ни в заказах, ни в записях — иначе
 * 409).
 *
 * Подтверждение (training-packages-approval; ТЗ 26.10.2026, п.4.4.3): добавить программу может
 * любой сотрудник команды — у администратора она действует сразу, у остальных ждёт
 * подтверждения («На подтверждении»). Подтверждает, отклоняет (с причиной), правит и убирает в
 * архив только администратор; автор может удалить своё неподтверждённое или отклонённое.
 */
import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Archive,
  ArchiveRestore,
  Check,
  ChevronDown,
  FolderInput,
  GraduationCap,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import {
  adminApi,
  teamApi,
  trainingApi,
  ApiError,
  type CatalogStatus,
  type CreateProgramInput,
  type EducationBase,
  type Program,
  type RecurrenceMode,
  type TrainingDirectionItem,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'
import {
  EDUCATION_BASE_LABELS,
  directionPathLabel,
  flattenDirections,
  formatHours,
  formatRubles,
  periodLabel,
  type DirectionNode,
} from '@/lib/training-catalog'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { CatalogStatusBadge, ProposalMeta, RejectDialog, useCatalogConfirm } from './catalog-shared'

/** Что показывать в списке: действующие, предложения на подтверждении, отклонённые, архив. */
export type ProgramsShow = 'active' | 'pending' | 'rejected' | 'archived'

function programShow(p: { status: CatalogStatus; archived: boolean }): ProgramsShow {
  if (p.status !== 'active') return p.status
  return p.archived ? 'archived' : 'active'
}

interface ProgramDraft {
  name: string
  shortName: string
  code: string
  /** Примечание (centrika-docs!53) — необязательное, переносы строк сохраняются. */
  note: string
  directionId: string
  /** Режим периодичности (centrika-docs!54): months|employer|none. */
  mode: RecurrenceMode
  recurrenceMonths: string
  /** Подпись периодичности «как в каталоге» для режимов без числа лет. */
  periodText: string
  hours: string
  documentType: string
  /** «Подлежат обучению» — по пункту на строку. */
  audience: string
  rank: string
  profStandard: string
  educationBase: EducationBase
  /** Цена в рублях (целое). */
  price: string
  ucSection: string
  /** Индекс программы: «А», «Б», «ПП», «СИЗ», «В1»… — свободный (ответ заказчицы 04.11 к п.5.2). */
  otIndex: string
}

/** Стили многострочного поля — те же токены, что у Input. */
const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

/** Варианты периодичности — подписи по требованию (порядок фиксирован). */
const RECURRENCE_OPTIONS: { mode: RecurrenceMode; label: string }[] = [
  { mode: 'months', label: 'Указать периодичность' },
  { mode: 'employer', label: 'Периодичность обучения определяет работодатель' },
  { mode: 'none', label: 'Без срока (повторное обучение не требуется)' },
]

/** Подсказки индекса в форме — вместе с уже заданными в каталоге. */
const OT_INDEX_SUGGESTIONS = ['А', 'Б', 'В', 'В1', 'В2', 'В3', 'ПП', 'СИЗ']

/** Формат индекса — как на сервере (domain.ValidOtIndex): буква и до пяти букв или цифр. */
const OT_INDEX_PATTERN = /^[A-ZА-ЯЁ][A-ZА-ЯЁ0-9]{0,5}$/

/** Индекс без пробелов, заглавными: «в1 » → «В1». */
export function normalizeOtIndex(v: string): string {
  return v.trim().toUpperCase()
}

/** Выделение выбранного варианта — акцентный контур на светлой заливке (как YesNoGroup). */
const SELECTED_OPTION = 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg'

function emptyDraft(defaultDirectionId: string): ProgramDraft {
  return {
    name: '',
    shortName: '',
    code: '',
    note: '',
    directionId: defaultDirectionId,
    mode: 'months',
    recurrenceMonths: '36',
    periodText: '',
    hours: '',
    documentType: '',
    audience: '',
    rank: '',
    profStandard: '',
    educationBase: 'any',
    price: '',
    ucSection: '',
    otIndex: '',
  }
}

function draftFromProgram(p: Program): ProgramDraft {
  return {
    name: p.name,
    shortName: p.shortName,
    code: p.code,
    note: p.note,
    directionId: p.directionId,
    mode: p.recurrenceMode,
    recurrenceMonths: p.recurrenceMonths === null ? '36' : String(p.recurrenceMonths),
    periodText: p.periodText,
    hours: p.hours === null ? '' : String(p.hours),
    documentType: p.documentType,
    audience: p.audience.join('\n'),
    rank: p.rank,
    profStandard: p.profStandard,
    educationBase: p.educationBase,
    price: p.priceKopecks == null ? '' : String(Math.round(p.priceKopecks / 100)),
    ucSection: p.ucSection ?? '',
    otIndex: p.otIndex ?? '',
  }
}

/** Тело запроса из черновика формы: месяцы — только в режиме `months`, подпись — в остальных. */
function draftToInput(d: ProgramDraft): CreateProgramInput {
  return {
    name: d.name.trim(),
    shortName: d.shortName.trim(),
    code: d.code.trim() || undefined,
    // Примечание сохраняется как есть (переносы строк сохраняются, пробелы не обрезаются).
    note: d.note,
    directionId: d.directionId,
    recurrenceMode: d.mode,
    recurrenceMonths: d.mode === 'months' ? Number(d.recurrenceMonths) : null,
    periodText: d.mode === 'months' ? '' : d.periodText.trim(),
    hours: d.hours.trim() ? Number(d.hours) : null,
    documentType: d.documentType.trim(),
    audience: d.audience
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean),
    rank: d.rank.trim(),
    profStandard: d.profStandard.trim(),
    educationBase: d.educationBase,
    priceKopecks: d.price.trim() ? Number(d.price) * 100 : null,
    ucSection: d.ucSection.trim(),
    otIndex: normalizeOtIndex(d.otIndex),
  }
}

function isPositiveInt(v: string): boolean {
  return Number.isInteger(Number(v)) && Number(v) > 0
}

/** Выбор раздела дерева: пункты с отступом по уровню. */
function DirectionSelect({
  value,
  onChange,
  nodes,
  ariaLabel,
  id,
  required,
  allLabel,
}: {
  value: string
  onChange: (v: string) => void
  nodes: DirectionNode[]
  ariaLabel: string
  id?: string
  /** Обязательность для вспомогательных технологий (маркер «*» у подписи от них скрыт). */
  required?: true | undefined
  /** Пункт «все разделы» (для фильтра): значение `all`. */
  allLabel?: string
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} aria-label={ariaLabel} aria-required={required}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allLabel ? <SelectItem value="all">{allLabel}</SelectItem> : null}
        {nodes.map((n) => (
          <SelectItem key={n.direction.id} value={n.direction.id}>
            <span style={{ paddingLeft: `${(n.depth - 1) * 16}px` }}>{n.direction.name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  error,
  required,
  inputMode,
  className,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
  error?: string
  required?: boolean
  inputMode?: 'numeric'
  className?: string
}) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={cn('my-0', className)}>
      {(props) => (
        <Input
          {...props}
          value={value}
          inputMode={inputMode}
          invalid={Boolean(error)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </Field>
  )
}

function ProgramForm({
  draft,
  setDraft,
  sectionLabel,
  indexSuggestions,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  codeRequired,
}: {
  draft: ProgramDraft
  setDraft: (d: ProgramDraft) => void
  /** Раздел, в котором программа (поля «Раздел» в форме нет — ответ заказчицы 04.11 к п.4.6). */
  sectionLabel: string
  /** Индексы для подсказки в поле «Индекс». */
  indexSuggestions: string[]
  busy: boolean
  submitLabel: string
  onSubmit: () => void
  onCancel?: () => void
  /** Код курса обязателен у новой программы — он ключ строки файла каталога. */
  codeRequired?: boolean
}) {
  const set = (patch: Partial<ProgramDraft>) => setDraft({ ...draft, ...patch })
  const monthsInvalid = draft.mode === 'months' && !isPositiveInt(draft.recurrenceMonths)
  const hoursInvalid = draft.hours.trim() !== '' && !isPositiveInt(draft.hours)
  const priceInvalid =
    draft.price.trim() !== '' && !(Number.isInteger(Number(draft.price)) && Number(draft.price) >= 0)
  const indexInvalid =
    normalizeOtIndex(draft.otIndex) !== '' && !OT_INDEX_PATTERN.test(normalizeOtIndex(draft.otIndex))
  const indexListId = `program-index-options-${draft.code || 'new'}`
  const canSubmit =
    draft.name.trim().length > 0 &&
    (!codeRequired || draft.code.trim().length > 0) &&
    draft.directionId !== '' &&
    !monthsInvalid &&
    !hoursInvalid &&
    !priceInvalid &&
    !indexInvalid

  function submit(e: FormEvent) {
    e.preventDefault()
    if (canSubmit && !busy) onSubmit()
  }

  return (
    <form onSubmit={submit} noValidate>
      <p className="mb-3 text-13 text-txt-secondary">
        Раздел: <span className="font-medium text-txt">{sectionLabel || '—'}</span>
      </p>
      <div className="grid grid-cols-1 gap-3 nw:grid-cols-2">
        <TextField
          label="Полное наименование"
          required
          value={draft.name}
          onChange={(name) => set({ name })}
          placeholder="Официальное название программы"
          className="nw:col-span-2"
        />
        <TextField
          label="Краткое наименование"
          hint="Показывается жирным в выборе программ; пусто — полное"
          value={draft.shortName}
          onChange={(shortName) => set({ shortName })}
        />
        <TextField
          label="Код курса"
          required={codeRequired}
          value={draft.code}
          onChange={(code) => set({ code })}
          placeholder="напр. ИД46А"
        />
        <TextField
          label="Раздел каталога УЦ"
          hint="Для выгрузки в учебный центр и Битрикс"
          value={draft.ucSection}
          onChange={(ucSection) => set({ ucSection })}
        />
        <TextField
          label="Кол-во часов"
          inputMode="numeric"
          value={draft.hours}
          onChange={(hours) => set({ hours })}
          error={hoursInvalid ? 'Целое число больше нуля' : undefined}
        />
        <TextField
          label="Выдаваемый документ"
          value={draft.documentType}
          onChange={(documentType) => set({ documentType })}
          placeholder="напр. Удостоверение о ПК"
        />
        <Field label="Подлежат обучению" hint="По одному пункту на строку" className="my-0 nw:col-span-2">
          {(props) => (
            <textarea
              {...props}
              value={draft.audience}
              onChange={(e) => set({ audience: e.target.value })}
              rows={3}
              className={TEXTAREA_CLASS}
            />
          )}
        </Field>
        <TextField label="Присваиваемый разряд" value={draft.rank} onChange={(rank) => set({ rank })} placeholder="напр. Нач: 2" />
        <TextField label="Профстандарт" value={draft.profStandard} onChange={(profStandard) => set({ profStandard })} />
        <Field label="База (образование)" className="my-0">
          {(props) => (
            <Select value={draft.educationBase} onValueChange={(v) => set({ educationBase: v as EducationBase })}>
              <SelectTrigger id={props.id} aria-label="База (образование)">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(EDUCATION_BASE_LABELS) as EducationBase[]).map((b) => (
                  <SelectItem key={b} value={b}>
                    {EDUCATION_BASE_LABELS[b]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <TextField
          label="Цена, ₽"
          hint="Видна только сотрудникам Центрики"
          inputMode="numeric"
          value={draft.price}
          onChange={(price) => set({ price })}
          error={priceInvalid ? 'Целое число рублей' : undefined}
        />
        <Field
          label="Индекс"
          hint="Например А, Б, ПП, СИЗ, В1 — показывается рядом с часами и в карточке сотрудника"
          error={indexInvalid ? 'Буква и до пяти букв или цифр, например В1' : undefined}
          className="my-0"
        >
          {(props) => (
            <>
              <Input
                {...props}
                value={draft.otIndex}
                list={indexListId}
                invalid={indexInvalid}
                onChange={(e) => set({ otIndex: e.target.value })}
                onBlur={() => set({ otIndex: normalizeOtIndex(draft.otIndex) })}
                placeholder="Нет"
              />
              <datalist id={indexListId}>
                {indexSuggestions.map((idx) => (
                  <option key={idx} value={idx} />
                ))}
              </datalist>
            </>
          )}
        </Field>
        <Field label="Примечание" className="my-0 nw:col-span-2">
          {(props) => (
            <textarea
              {...props}
              value={draft.note}
              onChange={(e) => set({ note: e.target.value })}
              rows={2}
              className={TEXTAREA_CLASS}
              placeholder="Необязательно — заметка о программе (видна клиенту при заказе)"
            />
          )}
        </Field>
      </div>
      {/* Периодичность обучения — один из трёх взаимоисключающих режимов (centrika-docs!54).
          Сегментированный выбор кнопками по принятому в проекте паттерну (см. YesNoGroup). */}
      <div className="mt-3">
        <p className="mb-1.5 text-13 font-medium text-txt">Периодичность обучения</p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Периодичность обучения">
          {RECURRENCE_OPTIONS.map((opt) => (
            <Button
              key={opt.mode}
              type="button"
              size="sm"
              variant="secondary"
              aria-pressed={draft.mode === opt.mode}
              className={cn(draft.mode === opt.mode && SELECTED_OPTION)}
              onClick={() => set({ mode: opt.mode })}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>
      {draft.mode === 'months' ? (
        <Field
          label="Периодичность (мес.)"
          required
          hint="Раз в сколько месяцев повторное обучение (3 года = 36)"
          error={monthsInvalid ? 'Укажите целое число месяцев > 0' : undefined}
          className="mt-3 max-w-xs"
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              min={1}
              value={draft.recurrenceMonths}
              invalid={monthsInvalid}
              onChange={(e) => set({ recurrenceMonths: e.target.value })}
            />
          )}
        </Field>
      ) : (
        <TextField
          label="Подпись периодичности"
          hint="Как в каталоге; пусто — по выбранному варианту"
          value={draft.periodText}
          onChange={(periodText) => set({ periodText })}
          placeholder={draft.mode === 'none' ? 'Бессрочно' : 'Определяет работодатель'}
          className="mt-3 max-w-md"
        />
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={!canSubmit || busy}>
          {busy ? 'Сохранение…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onCancel}
            leftIcon={<X className="size-4 stroke-current" aria-hidden />}
          >
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  )
}

/**
 * Ячейка программы: краткое жирно, ниже бледно — полное (если отличается); затем часы и
 * документ, периодичность и база (раздел — строкой дерева выше). Всё в одной ячейке — таблица
 * остаётся узкой.
 */
function ProgramCell({ p }: { p: Program }) {
  const short = p.shortName || p.name
  const meta = [formatHours(p.hours), p.documentType].filter(Boolean).join(' · ')
  return (
    <div className="min-w-0">
      <div className="break-words font-medium text-txt">
        {short}
        <CatalogStatusBadge status={p.status} archived={p.archived} />
      </div>
      {short !== p.name ? <div className="break-words text-12 text-txt-secondary">{p.name}</div> : null}
      {meta ? <div className="text-12 text-txt-tertiary">{meta}</div> : null}
      <div className="flex flex-wrap gap-x-1 text-12 text-txt-tertiary">
        <span>{periodLabel(p)}</span>
        <span aria-hidden>·</span>
        <span>{EDUCATION_BASE_LABELS[p.educationBase]}</span>
      </div>
      <ProposalMeta status={p.status} createdByName={p.createdByName} createdAt={p.createdAt} reviewNote={p.reviewNote} />
    </div>
  )
}

/** Колонок в таблице дерева: код, индекс, программа, цена, действия. */
const TREE_COLUMNS = 5

/** Раздел для программ, чьего раздела нет в дереве (не должно быть) — в конце. */
const ORPHAN_SECTION_ID = '__orphans__'

interface ProgramTreeSection {
  node: DirectionNode
  /** Родители раздела от корня — раздел виден, когда они раскрыты. */
  ancestors: string[]
  /** Программы самого раздела (без подразделов). */
  programs: Program[]
  /** Программ в разделе вместе с подразделами. */
  total: number
}

/** Разделы дерева в порядке каталога с программами и их числом (с подразделами). */
export function buildProgramTree(nodes: DirectionNode[], programs: Program[]) {
  const byDir = new Map<string, Program[]>()
  for (const p of programs) byDir.set(p.directionId, [...(byDir.get(p.directionId) ?? []), p])
  const sections: ProgramTreeSection[] = []
  const byId = new Map<string, ProgramTreeSection>()
  for (const node of nodes) {
    const parent = node.direction.parentId ? byId.get(node.direction.parentId) : undefined
    const sec: ProgramTreeSection = {
      node,
      ancestors: parent ? [...parent.ancestors, parent.node.direction.id] : [],
      programs: byDir.get(node.direction.id) ?? [],
      total: 0,
    }
    byId.set(node.direction.id, sec)
    sections.push(sec)
  }
  const orphans = programs.filter((p) => !byId.has(p.directionId))
  if (orphans.length > 0) {
    sections.push({
      node: {
        direction: { id: ORPHAN_SECTION_ID, name: 'Без раздела', parentId: '', sort: 0, systemCode: '', system: false },
        depth: 1,
        path: ['Без раздела'],
      },
      ancestors: [],
      programs: orphans,
      total: 0,
    })
  }
  // Число с подразделами: каждый раздел добавляет свои программы себе и всем родителям.
  for (const sec of sections) {
    for (const id of [...sec.ancestors, sec.node.direction.id]) {
      const target = id === sec.node.direction.id ? sec : byId.get(id)
      if (target) target.total += sec.programs.length
    }
  }
  return { sections }
}

const SHOW_LABELS: Record<ProgramsShow, string> = {
  active: 'Действующие',
  pending: 'На подтверждении',
  rejected: 'Отклонённые',
  archived: 'Архив',
}

export function ProgramsTab({
  isAdmin,
  accountId,
  initialShow = 'active',
}: {
  /** Администратор: подтверждает, правит, убирает в архив. Остальная команда — добавляет. */
  isAdmin: boolean
  /** Вход текущего пользователя — своё неподтверждённое он может удалить. */
  accountId: string
  initialShow?: ProgramsShow
}) {
  const [programs, setPrograms] = useState<Program[]>([])
  const [directions, setDirections] = useState<TrainingDirectionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const nodes = useMemo(() => flattenDirections(directions), [directions])

  // Новая программа добавляется в раздел, из которого её начали («Добавить программу» в строке
  // раздела): поля «Раздел» в форме нет (ответ заказчицы 04.11 к п.4.6).
  const [createDirectionId, setCreateDirectionId] = useState<string | null>(null)
  const [createDraft, setCreateDraft] = useState<ProgramDraft>(() => emptyDraft(''))
  const [creating, setCreating] = useState(false)
  // Перенос программы в другой раздел — отдельным действием.
  const [moving, setMoving] = useState<Program | null>(null)
  const [moveTo, setMoveTo] = useState('')
  const [movingBusy, setMovingBusy] = useState(false)
  // Раскрытые разделы дерева: изначально всё свёрнуто, поиск и фильтр раскрывают найденное.
  const [open, setOpen] = useState<Set<string>>(() => new Set())

  const [editId, setEditId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<ProgramDraft>(() => emptyDraft(''))
  const [savingEdit, setSavingEdit] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<Program | null>(null)
  // «В архив» и «Удалить» — с подтверждением (training-review-3).
  const [confirmDialog, confirmAction] = useCatalogConfirm()

  // Поиск и фильтр по статусу; раздел — деревом.
  const [query, setQuery] = useState('')
  const [show, setShow] = useState<ProgramsShow>(initialShow)
  // Переход из «колокольчика» при уже открытой вкладке: фильтр — из адреса.
  useEffect(() => setShow(initialShow), [initialShow])
  const counts = useMemo(() => {
    const out: Record<ProgramsShow, number> = { active: 0, pending: 0, rejected: 0, archived: 0 }
    for (const p of programs) out[programShow(p)]++
    return out
  }, [programs])
  const filteredPrograms = useMemo(() => {
    const q = query.trim().toLowerCase().replaceAll('ё', 'е')
    return programs.filter((p) => {
      if (programShow(p) !== show) return false
      if (!q) return true
      return [p.code, p.name, p.shortName].some((v) => v.toLowerCase().replaceAll('ё', 'е').includes(q))
    })
  }, [programs, query, show])
  // Индексы, уже заданные в каталоге, — подсказки в форме.
  const indexSuggestions = useMemo(
    () =>
      [...new Set([...OT_INDEX_SUGGESTIONS, ...programs.map((p) => p.otIndex ?? '').filter(Boolean)])].sort(
        (a, b) => a.localeCompare(b, 'ru', { numeric: true }),
      ),
    [programs],
  )
  const tree = useMemo(() => buildProgramTree(nodes, filteredPrograms), [nodes, filteredPrograms])
  const searching = query.trim() !== ''
  // Без поиска у действующих видны все разделы (в пустой можно добавить программу), иначе —
  // только разделы с найденным.
  const showEmptySections = !searching && show === 'active'
  // Поиск и смена статуса раскрывают разделы с найденным; без них — всё свёрнуто. Пересчёт — и
  // после загрузки каталога (вкладка может открыться сразу на «На подтверждении»).
  useEffect(() => {
    if (!searching && show === 'active') {
      setOpen(new Set())
      return
    }
    setOpen(new Set(tree.sections.filter((x) => x.total > 0).map((x) => x.node.direction.id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, show, loading])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    Promise.all([
      trainingApi.listPrograms(controller.signal),
      trainingApi.listDirections(controller.signal),
    ])
      .then(([progs, dirs]) => {
        if (controller.signal.aborted) return
        setPrograms(progs)
        setDirections(dirs)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить каталог'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  function toggleSection(id: string) {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** «Добавить программу» в раздел: форма — в этом разделе, он раскрывается. */
  function startCreate(directionId: string) {
    setEditId(null)
    setCreateDirectionId(directionId)
    setCreateDraft(emptyDraft(directionId))
    setOpen((prev) => new Set(prev).add(directionId))
  }

  async function move() {
    if (!moving || !moveTo || moveTo === moving.directionId) return
    setActionError(null)
    setMovingBusy(true)
    try {
      const updated = await adminApi.training.updateProgram(
        moving.id,
        draftToInput({ ...draftFromProgram(moving), directionId: moveTo }),
      )
      setPrograms((prev) => prev.map((x) => (x.id === moving.id ? updated : x)))
      setOpen((prev) => new Set(prev).add(moveTo))
      setNotice(
        `Программа «${updated.shortName || updated.name}» перенесена в раздел «${directionPathLabel(nodes, moveTo)}».`,
      )
      setMoving(null)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось перенести программу'))
    } finally {
      setMovingBusy(false)
    }
  }

  async function create() {
    setActionError(null)
    setNotice(null)
    setCreating(true)
    try {
      const program = await teamApi.training.createProgram(draftToInput(createDraft))
      setPrograms((prev) => [...prev, program])
      setCreateDirectionId(null)
      // Сброс фильтров, чтобы созданная программа не «пропала» под активным фильтром.
      setQuery('')
      setShow(programShow(program))
      setOpen((prev) => new Set(prev).add(program.directionId))
      if (program.status === 'pending') {
        setNotice(
          `Программа «${program.shortName || program.name}» отправлена на подтверждение. Клиенты увидят её после подтверждения администратором.`,
        )
      }
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось создать программу'))
    } finally {
      setCreating(false)
    }
  }

  function startEdit(p: Program) {
    setCreateDirectionId(null)
    setEditId(p.id)
    setEditDraft(draftFromProgram(p))
  }

  async function saveEdit() {
    if (!editId) return
    setActionError(null)
    setSavingEdit(true)
    try {
      const updated = await adminApi.training.updateProgram(editId, draftToInput(editDraft))
      setPrograms((prev) => prev.map((p) => (p.id === editId ? updated : p)))
      setEditId(null)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось сохранить программу'))
    } finally {
      setSavingEdit(false)
    }
  }

  async function setArchived(p: Program, archived: boolean) {
    setActionError(null)
    setBusyId(p.id)
    try {
      const updated = await adminApi.training.archiveProgram(p.id, archived)
      setPrograms((prev) => prev.map((x) => (x.id === p.id ? updated : x)))
    } catch (err) {
      setActionError(
        getErrorMessage(err, archived ? 'Не удалось убрать программу в архив' : 'Не удалось вернуть программу'),
      )
    } finally {
      setBusyId(null)
    }
  }

  async function review(p: Program, decision: 'approve' | 'reject', reason = '') {
    setActionError(null)
    setNotice(null)
    setBusyId(p.id)
    try {
      const updated = await adminApi.training.reviewProgram(
        p.id,
        decision === 'approve' ? { decision } : { decision, reason },
      )
      setPrograms((prev) => prev.map((x) => (x.id === p.id ? updated : x)))
      setRejecting(null)
      setNotice(
        decision === 'approve'
          ? `Программа «${updated.shortName || updated.name}» подтверждена и действует в каталоге.`
          : `Программа «${updated.shortName || updated.name}» отклонена, автор получит уведомление.`,
      )
    } catch (err) {
      setActionError(getErrorMessage(err, decision === 'approve' ? 'Не удалось подтвердить программу' : 'Не удалось отклонить программу'))
    } finally {
      setBusyId(null)
    }
  }

  async function remove(p: Program) {
    setActionError(null)
    setBusyId(p.id)
    try {
      await teamApi.training.deleteProgram(p.id)
      setPrograms((prev) => prev.filter((x) => x.id !== p.id))
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setActionError(
          `Программа «${p.shortName || p.name}» используется в заказах, записях обучения или пакетах — удалить нельзя. Уберите её в архив: в выборе она не появится, а история сохранится.`,
        )
      } else {
        setActionError(getErrorMessage(err, 'Не удалось удалить программу'))
      }
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}
      {notice ? (
        <Alert variant="info" className="mb-4">
          {notice}
        </Alert>
      ) : null}
      {isAdmin && counts.pending > 0 && show !== 'pending' ? (
        <Alert variant="info" className="mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <span>На подтверждении программ: {counts.pending}</span>
            <Button size="sm" variant="secondary" onClick={() => setShow('pending')}>
              Показать
            </Button>
          </div>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : programs.length === 0 && nodes.length === 0 ? (
        <Panel>
          <EmptyState
            icon={GraduationCap}
            title="Программ пока нет"
            description="Добавьте программу или загрузите файл каталога на вкладке «Загрузка и выгрузка»"
          />
        </Panel>
      ) : (
        <Panel>
          <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
            <Field label="Поиск" className="my-0 min-w-56 flex-1">
              {(props) => (
                <Input
                  {...props}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Код или наименование"
                />
              )}
            </Field>
            <Field label="Показать" className="my-0 w-full nw:w-60">
              {(props) => (
                <Select value={show} onValueChange={(v) => setShow(v as ProgramsShow)}>
                  <SelectTrigger id={props.id} aria-label="Показать программы">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(SHOW_LABELS) as ProgramsShow[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {SHOW_LABELS[k]}
                        {k === 'active' ? '' : ` (${counts[k]})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <p className="ml-auto text-13 text-txt-secondary">
              Действующих программ: {counts.active}
              {filteredPrograms.length !== counts[show] ? ` · показано ${filteredPrograms.length}` : ''}
            </p>
            <Button
              size="sm"
              variant="secondary"
              disabled={open.size === 0}
              onClick={() => setOpen(new Set())}
            >
              Свернуть все
            </Button>
          </div>
          <p className="border-b border-line px-4 py-2 text-13 text-txt-secondary">
            Новая программа добавляется в раздел — кнопкой «Добавить программу» в его строке.
          </p>
          {filteredPrograms.length === 0 && !showEmptySections ? (
            <p className="px-4 py-10 text-center text-txt-secondary">
              {show === 'pending' && counts.pending === 0 ? 'Программ на подтверждении нет' : 'По выбранным условиям программ нет'}
            </p>
          ) : (
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Код</DataTableHeaderCell>
                  <DataTableHeaderCell>Индекс</DataTableHeaderCell>
                  <DataTableHeaderCell>Программа</DataTableHeaderCell>
                  <DataTableHeaderCell>Цена</DataTableHeaderCell>
                  <DataTableHeaderCell>{''}</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {tree.sections.map((sec) => {
                  const id = sec.node.direction.id
                  // Раздел виден, когда раскрыты все его родители (и в нём есть программы — если
                  // пустые разделы сейчас не показываются).
                  if (!sec.ancestors.every((a) => open.has(a))) return null
                  if (!showEmptySections && sec.total === 0) return null
                  const isOpen = open.has(id)
                  return (
                    <Fragment key={id}>
                      <DataTableGroupRow colSpan={TREE_COLUMNS}>
                        <div
                          className="flex flex-wrap items-center gap-2"
                          style={{ paddingLeft: `${(sec.node.depth - 1) * 20}px` }}
                        >
                          <button
                            type="button"
                            className="ds-focus-ring flex min-w-0 flex-1 items-center gap-2 rounded-7 text-left normal-case"
                            aria-expanded={isOpen}
                            onClick={() => toggleSection(id)}
                          >
                            <ChevronDown
                              aria-hidden
                              className={cn('size-4 shrink-0 stroke-current transition-transform', !isOpen && '-rotate-90')}
                            />
                            <span className="min-w-0 break-words text-txt">{sec.node.direction.name}</span>
                            <span className="whitespace-nowrap font-normal text-txt-tertiary">
                              {sec.total}
                            </span>
                          </button>
                          {sec.node.direction.id !== ORPHAN_SECTION_ID ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              className="normal-case"
                              aria-label={`Добавить программу в раздел «${sec.node.direction.name}»`}
                              onClick={() => startCreate(id)}
                              leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
                            >
                              Добавить программу
                            </Button>
                          ) : null}
                        </div>
                      </DataTableGroupRow>
                      {isOpen && createDirectionId === id ? (
                        <DataTableRow>
                          <DataTableCell colSpan={TREE_COLUMNS}>
                            <p className="mb-2 font-semibold text-txt">Новая программа</p>
                            {!isAdmin ? (
                              <p className="mb-3 text-14 text-txt-secondary">
                                Программа появится в каталоге после подтверждения администратором.
                              </p>
                            ) : null}
                            <ProgramForm
                              draft={createDraft}
                              setDraft={setCreateDraft}
                              sectionLabel={directionPathLabel(nodes, id)}
                              indexSuggestions={indexSuggestions}
                              busy={creating}
                              submitLabel={isAdmin ? 'Добавить программу' : 'Отправить на подтверждение'}
                              onSubmit={() => void create()}
                              onCancel={() => setCreateDirectionId(null)}
                              codeRequired
                            />
                          </DataTableCell>
                        </DataTableRow>
                      ) : null}
                      {isOpen
                        ? sec.programs.map((p) =>
                            editId === p.id ? (
                              <DataTableRow key={p.id}>
                                <DataTableCell colSpan={TREE_COLUMNS}>
                                  <ProgramForm
                                    draft={editDraft}
                                    setDraft={setEditDraft}
                                    sectionLabel={directionPathLabel(nodes, p.directionId) || p.directionName}
                                    indexSuggestions={indexSuggestions}
                                    busy={savingEdit}
                                    submitLabel="Сохранить"
                                    onSubmit={() => void saveEdit()}
                                    onCancel={() => setEditId(null)}
                                  />
                                </DataTableCell>
                              </DataTableRow>
                            ) : (
                              <DataTableRow
                                key={p.id}
                                className={cn((p.archived || p.status === 'rejected') && 'opacity-70')}
                              >
                                <DataTableCell className="whitespace-nowrap text-txt-secondary">{p.code || '—'}</DataTableCell>
                                <DataTableCell className="whitespace-nowrap text-txt-secondary">{p.otIndex || '—'}</DataTableCell>
                                <DataTableCell>
                                  <ProgramCell p={p} />
                                </DataTableCell>
                                <DataTableCell className="whitespace-nowrap">{formatRubles(p.priceKopecks) || '—'}</DataTableCell>
                                <DataTableCell className="text-right">
                                  {/* Действия — иконками с подписью (aria-label + подсказка браузера):
                                      таблица на 238 программ должна помещаться по ширине. */}
                                  <div className="flex justify-end gap-1">
                                    <ProgramActions
                                      p={p}
                                      isAdmin={isAdmin}
                                      own={p.createdById === accountId}
                                      busy={busyId === p.id}
                                      onEdit={() => startEdit(p)}
                                      onMove={() => {
                                        setMoving(p)
                                        setMoveTo(p.directionId)
                                      }}
                                      onApprove={() => void review(p, 'approve')}
                                      onReject={() => setRejecting(p)}
                                      onArchive={(archived) =>
                                        archived
                                          ? confirmAction({
                                              title: `Убрать программу «${p.shortName || p.name}» в архив?`,
                                              message:
                                                'Программа пропадёт из выбора клиентов; пакеты с ней станут недоступны до правки состава. Сданные заказы и записи обучения не изменятся.',
                                              confirmLabel: 'В архив',
                                              onConfirm: () => void setArchived(p, true),
                                            })
                                          : void setArchived(p, false)
                                      }
                                      onRemove={() =>
                                        confirmAction({
                                          title: `Удалить программу «${p.shortName || p.name}»?`,
                                          message: 'Программа будет удалена из каталога насовсем.',
                                          confirmLabel: 'Удалить',
                                          danger: true,
                                          onConfirm: () => void remove(p),
                                        })
                                      }
                                    />
                                  </div>
                                </DataTableCell>
                              </DataTableRow>
                            ),
                          )
                        : null}
                    </Fragment>
                  )
                })}
              </DataTableBody>
            </DataTable>
          )}
        </Panel>
      )}
      {confirmDialog}
      <ConfirmDialog
        open={moving !== null}
        title={moving ? `Перенести программу «${moving.shortName || moving.name}»` : ''}
        message="Выберите раздел каталога, в который перенести программу."
        details={
          <DirectionSelect value={moveTo} nodes={nodes} ariaLabel="Раздел для переноса" onChange={setMoveTo} />
        }
        buttons={[
          {
            label: movingBusy ? 'Перенос…' : 'Перенести',
            variant: 'primary',
            disabled: movingBusy || !moving || moveTo === moving.directionId,
            onClick: () => void move(),
          },
          { label: 'Отмена', variant: 'secondary', disabled: movingBusy, onClick: () => setMoving(null) },
        ]}
        onClose={() => setMoving(null)}
      />
      <RejectDialog
        open={rejecting !== null}
        title={rejecting ? `Отклонить программу «${rejecting.shortName || rejecting.name}»?` : ''}
        busy={rejecting !== null && busyId === rejecting.id}
        onConfirm={(reason) => rejecting && void review(rejecting, 'reject', reason)}
        onClose={() => setRejecting(null)}
      />
    </div>
  )
}

/**
 * Действия строки иконками с подписью (aria-label + подсказка браузера): таблица на 238 программ
 * должна помещаться по ширине. Администратор: у неподтверждённой — «Подтвердить», «Отклонить»,
 * «Править», «Удалить»; у действующей — «Править», архив, «Удалить». Остальная команда — только
 * «Удалить» своё неподтверждённое или отклонённое.
 */
function ProgramActions({
  p,
  isAdmin,
  own,
  busy,
  onEdit,
  onMove,
  onApprove,
  onReject,
  onArchive,
  onRemove,
}: {
  p: Program
  isAdmin: boolean
  own: boolean
  busy: boolean
  onEdit: () => void
  /** «Перенести» в другой раздел (поля «Раздел» в форме нет). */
  onMove: () => void
  onApprove: () => void
  onReject: () => void
  onArchive: (archived: boolean) => void
  onRemove: () => void
}) {
  const label = p.shortName || p.name
  const icon = (Icon: typeof Pencil) => <Icon className="size-4 stroke-current" aria-hidden />
  const removeButton = (
    <Button
      size="sm"
      variant="secondary"
      aria-label={`Удалить «${label}»`}
      title="Удалить"
      disabled={busy}
      onClick={onRemove}
      leftIcon={icon(Trash2)}
    />
  )
  if (!isAdmin) return own && p.status !== 'active' ? removeButton : null
  return (
    <>
      {p.status !== 'active' ? (
        <Button
          size="sm"
          variant="secondary"
          aria-label={`Подтвердить «${label}»`}
          title="Подтвердить"
          disabled={busy}
          onClick={onApprove}
          leftIcon={icon(Check)}
        />
      ) : null}
      {p.status === 'pending' ? (
        <Button
          size="sm"
          variant="secondary"
          aria-label={`Отклонить «${label}»`}
          title="Отклонить"
          disabled={busy}
          onClick={onReject}
          leftIcon={icon(X)}
        />
      ) : null}
      <Button size="sm" variant="secondary" aria-label="Править" title="Править" onClick={onEdit} leftIcon={icon(Pencil)} />
      <Button
        size="sm"
        variant="secondary"
        aria-label={`Перенести «${label}» в другой раздел`}
        title="Перенести в другой раздел"
        disabled={busy}
        onClick={onMove}
        leftIcon={icon(FolderInput)}
      />
      {p.status === 'active' ? (
        <Button
          size="sm"
          variant="secondary"
          aria-label={p.archived ? 'Вернуть' : 'В архив'}
          title={p.archived ? 'Вернуть из архива' : 'Убрать в архив'}
          disabled={busy || (p.archived && (Boolean(p.replacedById) || Boolean(p.split)))}
          onClick={() => onArchive(!p.archived)}
          leftIcon={p.archived ? icon(ArchiveRestore) : icon(Archive)}
        />
      ) : null}
      {removeButton}
    </>
  )
}
